import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  ScheduledTask,
  TaskTemplate,
  SchedulerDaemonStatus,
  SchedulerAuditEntry,
  SchedulerSkillInfo,
  SchedulerConnectorInfo,
  GitConnectionInfo,
  GitProvider,
  FeishuConnectionInfo,
  FeishuScope,
  FeishuRecipientInfo,
} from "@shared/types";
import {
  integrationsApi,
  feishuApi,
  feishuRecipientsApi,
  providerDefaultBase,
  splitConnectionUrl,
  type FeishuChat,
  type RosterImportRow,
} from "../lib/integrationsApi";
import { getUser } from "../lib/auth";
import { BrandLogo } from "../components/BrandLogo";
import { SkillsManager } from "../skills/SkillsManager";
import { Markdown } from "../components/chat/Markdown";
import {
  IconPlus, IconClock, IconRefresh, IconFile, IconSpark,
  IconChart, IconActivity, IconFlag, IconWrench,
} from "../components/icons";
import {
  schedulerApi,
  scheduleLabel,
  riskBadge,
  friendlyError,
  connectorLabels,
  skillLabels,
  sourceFields,
  sourceSummary,
  webhookUrl,
  PRESET_OPTIONS,
  type CreateTaskBody,
} from "../lib/schedulerApi";
import { parseScheduleZh } from "../lib/parse-schedule";

/** Form prefill carried from a template (adds the gallery-only `intent` line). */
type FormDefaults = Partial<CreateTaskBody> & { intent?: string };

/** Map a task to a status-dot class (mirrors the design system semantics). */
function statusDot(status: ScheduledTask["status"]): string {
  switch (status) {
    case "active":
      return "ok";
    case "paused":
      return "warn";
    case "failed":
      return "err";
    default:
      return "off";
  }
}

const STATUS_LABEL: Record<ScheduledTask["status"], string> = {
  active: "活跃",
  paused: "暂停",
  completed: "已完成",
  failed: "失败",
};

/** Format an epoch-ms timestamp for the UI, or a dash when absent. */
function fmt(ms?: number): string {
  if (ms === undefined) return "—";
  const d = new Date(ms);
  return d.toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/**
 * Curated "suggested routines" (review §8.2). A few good starting points with a
 * one-line why; 采用 opens the wizard prefilled. (Data-driven suggestions from a
 * tenant's ticket distribution are a follow-up.)
 */
const SUGGESTIONS: { title: string; why: string; defaults: FormDefaults }[] = [
  {
    title: "每周一汇总上周高频问题",
    why: "把反复出现的问题理出来，省得每周翻一遍",
    defaults: {
      title: "每周高频问题汇总",
      jobType: "agent.prompt",
      permission: "assist",
      prompt: "读取代码仓库上周的 issue，找出反复出现/高频的问题，按主题归类，给我一份清单。",
      recurrencePreset: "weekly",
      jobParams: { connectors: ["git"] },
    },
  },
  {
    title: "每天把昨天的代码提交理一份简报",
    why: "改了什么一眼看完，不用翻 commit",
    defaults: {
      title: "每日代码变更简报",
      jobType: "agent.prompt",
      permission: "observe",
      prompt: "读取代码仓库最近 24 小时的提交，按主题归类、标出值得关注的改动，给我一份简报。",
      recurrencePreset: "weekdays",
      jobParams: { connectors: ["git"] },
    },
  },
];

/** localStorage key: the suggestion banner stays dismissed across sessions. */
const SUGGEST_DISMISS_KEY = "sch_suggest_dismissed";

/** Top-level scheduler page (admin sees all owners + daemon; agent sees own + quota). */
export function SchedulerPage(): JSX.Element {
  const navigate = useNavigate();
  const user = getUser();
  const isAdmin = user?.role === "admin";

  const [tasks, setTasks] = useState<ScheduledTask[]>([]);
  const [daemon, setDaemon] = useState<SchedulerDaemonStatus | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [formDefaults, setFormDefaults] = useState<FormDefaults | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ScheduledTask | null>(null);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [connOpen, setConnOpen] = useState(false);
  const [connVersion, setConnVersion] = useState(0);
  // Suggestion banner dismissal persists (one banner, stays gone — NN/g).
  const [suggestDismissed, setSuggestDismissed] = useState(() => {
    try {
      return localStorage.getItem(SUGGEST_DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  // The task awaiting a delete confirmation (null = no dialog open).
  const [pendingDelete, setPendingDelete] = useState<ScheduledTask | null>(null);
  // Task ids with a run in flight (task_triggered → terminal), for the 运行中 state.
  const [running, setRunning] = useState<Set<string>>(() => new Set());

  const dismissSuggest = (): void => {
    setSuggestDismissed(true);
    try {
      localStorage.setItem(SUGGEST_DISMISS_KEY, "1");
    } catch {
      /* private mode / storage disabled — banner stays dismissed for the session */
    }
  };

  const reload = useCallback(async () => {
    try {
      const { tasks: rows } = await schedulerApi.list(statusFilter.length > 0 ? { status: statusFilter } : {});
      setTasks(rows);
      if (isAdmin) setDaemon((await schedulerApi.daemonStatus()).daemon);
    } catch (e) {
      setError(friendlyError(e));
    }
  }, [statusFilter, isAdmin]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Live updates: reload the list whenever a task fires/completes/fails (SSE),
  // instead of polling. The same stream drives the "运行中" state — a task is
  // running from task_triggered until it reaches a terminal event.
  useEffect(() => {
    const ctrl = new AbortController();
    schedulerApi.subscribeEvents((e) => {
      const id = e.taskId;
      if (id !== undefined) {
        setRunning((prev) => {
          const next = new Set(prev);
          if (e.type === "task_triggered") next.add(id);
          else if (e.type === "task_completed" || e.type === "task_failed" || e.type === "task_auto_paused") {
            next.delete(id);
          }
          return next;
        });
      }
      void reload();
    }, ctrl.signal);
    return () => ctrl.abort();
  }, [reload]);

  const stats = useMemo(() => {
    const by = (s: ScheduledTask["status"]): number => tasks.filter((t) => t.status === s).length;
    return { active: by("active"), paused: by("paused"), failed: by("failed"), total: tasks.length };
  }, [tasks]);

  const onPickTemplate = (tmpl: TaskTemplate | null): void => {
    setGalleryOpen(false);
    // "From scratch" is a new prompt routine: the user writes an instruction for
    // 小环, no internal job type or permission jargon.
    setFormDefaults(
      tmpl === null
        ? { title: "", jobType: "agent.prompt", prompt: "", permission: "assist", recurrencePreset: "weekdays" }
        : {
            title: tmpl.name,
            jobType: tmpl.jobType,
            ...(tmpl.prompt !== undefined ? { prompt: tmpl.prompt } : {}),
            ...(tmpl.intent !== undefined ? { intent: tmpl.intent } : {}),
            ...(tmpl.triggerType !== undefined ? { triggerType: tmpl.triggerType } : {}),
            ...(tmpl.jobParams !== undefined ? { jobParams: tmpl.jobParams } : {}),
            description: tmpl.description,
            recurrencePreset: tmpl.recurrencePreset,
            priority: tmpl.priority,
            permission: tmpl.permission,
            tags: tmpl.tags,
          },
    );
  };

  // Start a new routine that activates an uploaded skill (the direct path from
  // the skill panel to a schedule).
  const onUseSkill = (s: SchedulerSkillInfo): void => {
    setSkillsOpen(false);
    setEditId(null);
    setFormDefaults({
      title: s.name,
      jobType: "agent.prompt",
      permission: "assist",
      prompt: "",
      intent: `运行技能「${s.name}」，在下面写清要它具体做什么。`,
      recurrencePreset: "weekdays",
      jobParams: { skills: [s.name] },
    });
  };

  // Open the form pre-filled from an existing task, in edit mode.
  const onEdit = (t: ScheduledTask): void => {
    setEditId(t.id);
    setFormDefaults({
      title: t.title,
      jobType: t.jobType,
      permission: t.permission,
      ...(t.prompt !== undefined ? { prompt: t.prompt } : {}),
      ...(t.description !== undefined ? { description: t.description } : {}),
      ...(t.recurrencePreset !== undefined ? { recurrencePreset: t.recurrencePreset } : {}),
      ...(t.priority !== undefined ? { priority: t.priority } : {}),
      ...(Object.keys(t.jobParams).length > 0 ? { jobParams: t.jobParams } : {}),
    });
  };

  const closeForm = (): void => {
    setFormDefaults(null);
    setEditId(null);
  };

  // Create/update a task. Returns the created task so the wizard can show a
  // webhook URL on its success step; returns null on edit or error.
  const onSubmit = async (body: CreateTaskBody): Promise<ScheduledTask | null> => {
    try {
      if (editId !== null) {
        const { task } = await schedulerApi.update(editId, body);
        await reload();
        return task;
      }
      const { task } = await schedulerApi.create(body);
      await reload();
      return task;
    } catch (e) {
      setError(friendlyError(e));
      return null;
    }
  };

  // Run now: mark the card 运行中 immediately (don't pop a modal — the card
  // shows progress in place). The SSE terminal event clears it; a timeout is a
  // safety net if the stream drops, so a card never sticks on 运行中.
  const onRun = async (t: ScheduledTask): Promise<void> => {
    setRunning((s) => new Set(s).add(t.id));
    await act(() => schedulerApi.runNow(t.id));
    window.setTimeout(() => setRunning((s) => {
      const n = new Set(s);
      n.delete(t.id);
      return n;
    }), 120_000);
  };

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    try {
      await fn();
      await reload();
    } catch (e) {
      setError(friendlyError(e));
    }
  };

  return (
    <div className="sch">
      <header className="sch-top">
        <div className="sch-top-l">
          <BrandLogo />
          <span className="sch-title">定时任务</span>
          <span className={`sch-role ${isAdmin ? "" : "agent"}`}>{isAdmin ? "管理视图" : "我的任务"}</span>
        </div>
        <div className="sch-top-r">
          {isAdmin && daemon !== null && (
            <span
              className={`chip ${daemon.status === "running" ? "ok" : "off"}`}
              title={daemon.status === "running" ? `每 ${Math.round(daemon.pollIntervalMs / 1000)} 秒检查一次` : daemon.status}
            >
              <span className={`dot ${daemon.status === "running" ? "run" : "off"}`} /> 调度服务{" "}
              {daemon.status === "running" ? "正常" : "已停"}
            </span>
          )}
          {!isAdmin && daemon === null && (
            <span className="chip off">配额上限 {/* shown after first create error */}</span>
          )}
          <button className="btn ghost" onClick={() => navigate("/")}>返回</button>
          {isAdmin && <button className="btn ghost" onClick={() => setConnOpen(true)}>集成</button>}
          {isAdmin && <button className="btn ghost" onClick={() => setSkillsOpen(true)}>技能</button>}
          <button className="btn" onClick={() => setGalleryOpen(true)}>
            <IconPlus /> 新建任务
          </button>
        </div>
      </header>

      {error !== null && (
        <div className="sch-error" role="alert">
          {error}
          <button className="sch-error-x" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {isAdmin && (
        <div className="sch-stats">
          <Stat label="活跃" value={stats.active} tone="ok" />
          <Stat label="暂停" value={stats.paused} tone="warn" />
          <Stat label="失败" value={stats.failed} tone="err" />
          <Stat label="全部" value={stats.total} />
        </div>
      )}

      <div className="sch-toolbar">
        {["", "active", "paused", "failed"].map((s) => (
          <button
            key={s.length > 0 ? s : "all"}
            className={`fchip ${statusFilter === s ? "on" : ""}`}
            onClick={() => setStatusFilter(s)}
          >
            {s.length === 0 ? "全部" : STATUS_LABEL[s as ScheduledTask["status"]]}
          </button>
        ))}
      </div>

      {!suggestDismissed && SUGGESTIONS[0] !== undefined && (
        <div className="sch-suggest">
          <span className="sch-suggest-glyph"><IconSpark /></span>
          <div className="sch-suggest-text">
            <div className="sch-suggest-title">建议：{SUGGESTIONS[0].title}</div>
            <div className="sch-suggest-why">{SUGGESTIONS[0].why} · 一键采用</div>
          </div>
          <button className="btn ghost sm" onClick={() => { setFormDefaults(SUGGESTIONS[0]!.defaults); setEditId(null); }}>采用</button>
          <button className="sch-suggest-x" title="不再提示" onClick={dismissSuggest}>×</button>
        </div>
      )}

      {tasks.length === 0 ? (
        <EmptyState onNew={() => setGalleryOpen(true)} />
      ) : (
        <div className="sch-cards">
          {tasks.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              showOwner={isAdmin}
              running={running.has(t.id)}
              onRun={() => void onRun(t)}
              onEdit={() => onEdit(t)}
              onToggle={() =>
                void act(() => schedulerApi.update(t.id, { status: t.status === "paused" ? "active" : "paused" }))
              }
              onDelete={() => setPendingDelete(t)}
              onOpen={() => setSelected(t)}
            />
          ))}
        </div>
      )}

      {galleryOpen && <TemplateGallery onPick={onPickTemplate} onClose={() => setGalleryOpen(false)} />}
      {formDefaults !== null && (
        <TaskForm
          defaults={formDefaults}
          isEdit={editId !== null}
          onSubmit={onSubmit}
          onClose={closeForm}
          onConfigure={() => setConnOpen(true)}
          connVersion={connVersion}
        />
      )}
      {selected !== null && (
        <TaskDetail
          task={selected}
          running={running.has(selected.id)}
          onRunAgain={() => void onRun(selected)}
          onToggle={() => {
            const next = selected.status === "paused" ? "active" : "paused";
            void act(() => schedulerApi.update(selected.id, { status: next }));
            setSelected({ ...selected, status: next });
          }}
          onEdit={() => { setSelected(null); onEdit(selected); }}
          onClose={() => setSelected(null)}
        />
      )}
      {skillsOpen && <SkillsManager onUse={onUseSkill} onClose={() => setSkillsOpen(false)} />}
      {connOpen && <GitConnections onClose={() => { setConnOpen(false); setConnVersion((v) => v + 1); }} />}
      {pendingDelete !== null && (
        <ConfirmDialog
          title="删除这个任务？"
          body={`「${pendingDelete.title}」以后不会再自动运行，执行历史也会一并清除。`}
          confirmLabel="删除"
          onConfirm={() => {
            const id = pendingDelete.id;
            setPendingDelete(null);
            void act(() => schedulerApi.remove(id));
          }}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}

/**
 * In-design confirm dialog — replaces the native window.confirm (which is
 * browser chrome and breaks the design language). Consequence copy, the
 * destructive action filled red; backdrop click, 取消, or Esc all dismiss.
 */
function ConfirmDialog(props: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
}): JSX.Element {
  const { onClose } = props;
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="sch-modal-bg" onClick={onClose}>
      <div className="sch-modal narrow" onClick={(e) => e.stopPropagation()}>
        <div className="sch-modal-head">
          <h3>{props.title}</h3>
        </div>
        <p className="sch-subtitle" style={{ marginTop: 0 }}>{props.body}</p>
        <div className="sch-form-actions">
          <button className="btn ghost" onClick={onClose}>取消</button>
          <button
            className="btn"
            style={{ background: "var(--color-error)", borderColor: "var(--color-error)" }}
            onClick={props.onConfirm}
          >
            {props.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** UI provider — git providers plus Feishu (a different credential shape). */
type UiProvider = "github" | "gitlab" | "feishu";

/** Admin panel: configure code-repo (GitHub/GitLab) + Feishu bot connections. */
function GitConnections(props: { onClose: () => void }): JSX.Element {
  const [provider, setProvider] = useState<UiProvider>("github");
  // git fields
  const [baseUrl, setBaseUrl] = useState(providerDefaultBase("github"));
  const [token, setToken] = useState("");
  const [repos, setRepos] = useState<string[]>([]);
  const [repo, setRepo] = useState("");
  const pendingRepo = useRef<string | null>(null);
  // feishu fields
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [chats, setChats] = useState<FeishuChat[]>([]);
  const [chatId, setChatId] = useState("");
  const [scopes, setScopes] = useState<FeishuScope[]>(["write"]);
  // shared
  const [label, setLabel] = useState("");
  const [valid, setValid] = useState<{ username?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [gitList, setGitList] = useState<GitConnectionInfo[] | null>(null);
  const [feishuList, setFeishuList] = useState<FeishuConnectionInfo[] | null>(null);

  const isFeishu = provider === "feishu";
  const gitProvider: GitProvider = provider === "gitlab" ? "gitlab" : "github";

  const load = useCallback(async () => {
    try {
      const [g, f] = await Promise.all([integrationsApi.list(), feishuApi.list()]);
      setGitList(g.connections);
      setFeishuList(f.connections);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const pickProvider = (p: UiProvider): void => {
    setProvider(p);
    if (p !== "feishu") setBaseUrl(providerDefaultBase(p));
    setValid(null);
    setRepos([]);
    setRepo("");
    setChats([]);
    setChatId("");
    setScopes(["write"]);
  };
  const wrap = (fn: () => Promise<void>): void => {
    setBusy(true);
    setErr(null);
    void fn()
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  // Paste-a-URL normalization: a repo/instance URL in the API field → API base
  // (+ remember the repo to auto-select after discover) instead of a 404.
  const normalizeBase = (): void => {
    if (isFeishu) return;
    const split = splitConnectionUrl(gitProvider, baseUrl);
    if (split !== null && split.baseUrl !== baseUrl) {
      setBaseUrl(split.baseUrl);
      if (split.repo !== undefined) pendingRepo.current = split.repo;
    }
  };

  const onValidate = (): void =>
    wrap(async () => {
      if (isFeishu) {
        const r = await feishuApi.validate(appId, appSecret);
        if (!r.valid) { setValid(null); setErr(r.error ?? "飞书校验失败：检查 App ID / Secret"); }
        else setValid({});
        return;
      }
      const r = await integrationsApi.validate(gitProvider, baseUrl, token);
      if (!r.valid) {
        setValid(null);
        let msg = r.error ?? "连接没成功：检查访问令牌是否过期，再试一次";
        if (gitProvider === "github" && msg.includes("404") && !baseUrl.includes("api.github.com")) {
          msg = "API 地址应为 https://api.github.com（仓库请用「发现仓库」选择，别把仓库网页地址填进 API 地址）。";
        }
        setErr(msg);
      } else {
        setValid({ ...(r.username !== undefined ? { username: r.username } : {}) });
      }
    });
  const onDiscover = (): void =>
    wrap(async () => {
      if (isFeishu) {
        const r = await feishuApi.discover(appId, appSecret);
        setChats(r.chats);
        if (r.chats.length > 0 && !r.chats.some((c) => c.chatId === chatId)) setChatId(r.chats[0]!.chatId);
        return;
      }
      const r = await integrationsApi.discover(gitProvider, baseUrl, token);
      setRepos(r.repos);
      if (r.repos.length > 0) {
        const pending = pendingRepo.current;
        if (pending !== null && r.repos.includes(pending)) setRepo(pending);
        else if (!r.repos.includes(repo)) setRepo(r.repos[0] ?? "");
      }
      pendingRepo.current = null;
    });
  const onSave = (): void =>
    wrap(async () => {
      if (isFeishu) {
        const chat = chats.find((c) => c.chatId === chatId);
        await feishuApi.create({
          label: label.trim().length > 0 ? label.trim() : "飞书 bot",
          appId, appSecret, chatId, scopes,
          ...(chat !== undefined ? { chatName: chat.name } : {}),
        });
        setAppId(""); setAppSecret(""); setChats([]); setChatId("");
      } else {
        await integrationsApi.create({
          provider: gitProvider, label: label.trim().length > 0 ? label.trim() : `${gitProvider} 连接`,
          baseUrl, token, ...(repo.length > 0 ? { repo } : {}), ...(repos.length > 0 ? { repos } : {}),
        });
        setToken(""); setRepo(""); setRepos([]);
      }
      setLabel("");
      setValid(null);
      await load();
    });
  const onDeleteGit = (id: string): void => wrap(async () => { await integrationsApi.remove(id); await load(); });
  const onDeleteFeishu = (id: string): void => wrap(async () => { await feishuApi.remove(id); await load(); });
  const toggleScope = (s: FeishuScope): void =>
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  // A dm-only bot addresses people (the roster), not a group, so it needs no chat;
  // a write/read (group) scope still requires a target chat.
  const hasGroupScope = scopes.includes("write") || scopes.includes("read");
  const canSave =
    !busy && valid !== null && (!isFeishu || (scopes.length > 0 && (!hasGroupScope || chatId.length > 0)));

  return (
    <div className="sch-modal-bg" onClick={props.onClose}>
      <div className="sch-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sch-modal-head">
          <div>
            <h3>集成连接 · 代码仓库 / 飞书</h3>
            <p className="sch-subtitle">配置代码仓库连接或飞书 bot，定时任务可 @ 引用它。凭据加密存储，不回显。</p>
          </div>
          <button className="btn ghost sm" onClick={props.onClose}>关闭</button>
        </div>

        {err !== null && <div className="sch-error" role="alert">{err}</div>}

        <div className="sch-field">
          <label>提供方</label>
          <div className="pillgroup">
            <span className={`spill ${provider === "github" ? "on" : ""}`} onClick={() => pickProvider("github")}>GitHub</span>
            <span className={`spill ${provider === "gitlab" ? "on" : ""}`} onClick={() => pickProvider("gitlab")}>GitLab</span>
            <span className={`spill ${provider === "feishu" ? "on" : ""}`} onClick={() => pickProvider("feishu")}>飞书</span>
          </div>
        </div>

        {!isFeishu && (
          <>
            <div className="sch-field">
              <label>{gitProvider === "github" ? "API 地址（GitHub Enterprise 改这里）" : "实例地址（自建 GitLab 改这里）"}</label>
              <input className="sch-in" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} onBlur={normalizeBase} />
            </div>
            <div className="sch-field">
              <label>访问令牌（Personal Access Token）</label>
              <input className="sch-in mono" type="password" value={token} placeholder={gitProvider === "github" ? "ghp_…" : "glpat-…"} onChange={(e) => { setToken(e.target.value); setValid(null); }} />
            </div>
          </>
        )}

        {isFeishu && (
          <>
            <div className="sch-field">
              <label>App ID</label>
              <input className="sch-in mono" value={appId} placeholder="cli_…" onChange={(e) => { setAppId(e.target.value); setValid(null); }} />
            </div>
            <div className="sch-field">
              <label>App Secret</label>
              <input className="sch-in mono" type="password" value={appSecret} placeholder="应用密钥" onChange={(e) => { setAppSecret(e.target.value); setValid(null); }} />
            </div>
          </>
        )}

        <div className="sch-field-row">
          <button className="btn ghost sm" disabled={busy || (isFeishu ? appId.length === 0 || appSecret.length === 0 : token.length === 0)} onClick={onValidate}>测试连接</button>
          <button className="btn ghost sm" disabled={busy || valid === null} onClick={onDiscover}>{isFeishu ? "发现群" : "发现仓库"}</button>
          {valid !== null && <span className="chip-ok">✓ 已验证{valid.username !== undefined ? `：${valid.username}` : ""}</span>}
        </div>

        {!isFeishu && repos.length > 0 && (
          <div className="sch-field">
            <label>{gitProvider === "github" ? "默认仓库" : "默认项目"}（共 {repos.length} 个）</label>
            <select className="sch-in" value={repo} onChange={(e) => setRepo(e.target.value)}>
              {repos.map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
          </div>
        )}

        {isFeishu && hasGroupScope && chats.length > 0 && (
          <div className="sch-field">
            <label>目标群（共 {chats.length} 个）</label>
            <select className="sch-in" value={chatId} onChange={(e) => setChatId(e.target.value)}>
              {chats.map((c) => (<option key={c.chatId} value={c.chatId}>{c.name}</option>))}
            </select>
          </div>
        )}

        {isFeishu && (
          <div className="sch-field">
            <label>权限</label>
            <div className="pillgroup">
              <span className={`spill ${scopes.includes("write") ? "on" : ""}`} onClick={() => toggleScope("write")}>写（发群消息）</span>
              <span className={`spill ${scopes.includes("read") ? "on" : ""}`} onClick={() => toggleScope("read")}>读（读群消息）</span>
              <span className={`spill ${scopes.includes("dm") ? "on" : ""}`} onClick={() => toggleScope("dm")}>私信（发给个人）</span>
            </div>
            {scopes.includes("dm") && !hasGroupScope && (
              <p className="sch-hint">私信认人不认群，无需选目标群。收件人在下方花名册里管理。</p>
            )}
          </div>
        )}

        <div className="sch-field">
          <label>名称（可选）</label>
          <input className="sch-in" value={label} placeholder={isFeishu ? "飞书 bot" : `${gitProvider} 连接`} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div className="sch-form-actions">
          <button className="btn" disabled={!canSave} onClick={onSave}>{busy ? "保存中…" : "保存连接"}</button>
        </div>

        <div className="sch-overline" style={{ marginTop: "var(--space-5)" }}>已配置</div>
        {gitList === null && feishuList === null && <p className="sch-muted">加载中…</p>}
        {gitList !== null && feishuList !== null && gitList.length === 0 && feishuList.length === 0 && (
          <p className="sch-muted">还没有连接。验证一个凭据即可保存。</p>
        )}
        {(gitList ?? []).map((c) => (
          <div key={c.id} className="sch-li">
            <div className="sch-li-main">
              <div className="sch-li-row">
                <span className="sch-li-name">{c.label}<span className="sch-skill-tag">{c.provider}</span></span>
                <span className="sch-li-actions">
                  <button className="btn ghost sm danger" onClick={() => onDeleteGit(c.id)}>删除</button>
                </span>
              </div>
              <div className="sch-li-desc">{c.username !== undefined ? `${c.username} · ` : ""}{c.repo ?? "—"} · {c.tokenMask}</div>
            </div>
          </div>
        ))}
        {(feishuList ?? []).map((c) => (
          <div key={c.id} className="sch-li">
            <div className="sch-li-main">
              <div className="sch-li-row">
                <span className="sch-li-name">{c.label}<span className="sch-skill-tag">飞书 · {feishuScopeText(c.scopes)}</span></span>
                <span className="sch-li-actions">
                  <button className="btn ghost sm danger" onClick={() => onDeleteFeishu(c.id)}>删除</button>
                </span>
              </div>
              <div className="sch-li-desc">{(c.chatName ?? c.chatId) || "（私信，无群）"} · {c.appId} · {c.secretMask}</div>
            </div>
          </div>
        ))}

        {isFeishu && <FeishuRoster connections={feishuList ?? []} />}
      </div>
    </div>
  );
}

/** Short scope label for a Feishu connection (写/读/私信). */
function feishuScopeText(scopes: FeishuScope[]): string {
  const parts: string[] = [];
  if (scopes.includes("write")) parts.push("写");
  if (scopes.includes("read")) parts.push("读");
  if (scopes.includes("dm")) parts.push("私信");
  return parts.length > 0 ? parts.join("/") : "读";
}

/** A pasted line "name,email,role" -> an import row (name required). */
function parseRosterLine(line: string): RosterImportRow | null {
  const cols = line.split(/[,，\t]/).map((s) => s.trim());
  const name = cols[0] ?? "";
  if (name.length === 0) return null;
  const second = cols[1] ?? "";
  const isMobile = /^\+?\d[\d\s-]{5,}$/.test(second);
  return {
    name,
    ...(second.length > 0 ? (isMobile ? { mobile: second } : { email: second }) : {}),
    ...((cols[2] ?? "").length > 0 ? { role: cols[2] } : {}),
  };
}

/**
 * Feishu DM recipient roster (feishu-dm-push.md): paste a name/email/role list,
 * resolve emails+mobiles to open_id via a chosen bot, and 补漏 the rest by hand.
 * open_id is the stable key the dm tool resolves names against.
 */
function FeishuRoster(props: { connections: FeishuConnectionInfo[] }): JSX.Element {
  const [rows, setRows] = useState<FeishuRecipientInfo[] | null>(null);
  const [paste, setPaste] = useState("");
  const [connId, setConnId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Prefer a dm-scoped connection for resolving; fall back to the first one.
  const dmConns = props.connections.filter((c) => c.scopes.includes("dm"));
  const resolvable = dmConns.length > 0 ? dmConns : props.connections;
  useEffect(() => {
    if (connId.length === 0 && resolvable[0] !== undefined) setConnId(resolvable[0].id);
  }, [connId, resolvable]);

  const load = useCallback(async () => {
    try {
      setRows((await feishuRecipientsApi.list()).recipients);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const run = (fn: () => Promise<void>): void => {
    setBusy(true); setErr(null); setNote(null);
    void fn().catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e))).finally(() => setBusy(false));
  };

  const onImport = (): void =>
    run(async () => {
      const parsed = paste.split("\n").map(parseRosterLine).filter((r): r is RosterImportRow => r !== null);
      if (parsed.length === 0) { setErr("没解析到有效行（每行：姓名,邮箱或手机号,角色）"); return; }
      const r = await feishuRecipientsApi.import(parsed);
      setRows(r.recipients); setPaste("");
      setNote(`已导入 ${parsed.length} 人，点「解析」补 open_id`);
    });

  const onResolve = (): void =>
    run(async () => {
      if (connId.length === 0) { setErr("先配置一个飞书 bot 用于解析"); return; }
      const r = await feishuRecipientsApi.resolve(connId);
      setRows(r.recipients);
      setNote(`解析完成：新解析 ${r.updated} 人${r.reason !== undefined ? ` · ${r.reason}` : ""}`);
    });

  const onFill = (rec: FeishuRecipientInfo): void => {
    const v = window.prompt(`给「${rec.name}」补一个 open_id（ou_…）或手机号：`, rec.openId ?? rec.mobile ?? "");
    if (v === null || v.trim().length === 0) return;
    const val = v.trim();
    const isOpenId = val.startsWith("ou_");
    run(async () => {
      await feishuRecipientsApi.upsert({
        id: rec.id, name: rec.name, role: rec.role,
        ...(rec.email !== undefined ? { email: rec.email } : {}),
        ...(isOpenId ? { openId: val } : { mobile: val }),
      });
      await load();
      setNote(isOpenId ? "已补 open_id" : "已补手机号，点「解析」生成 open_id");
    });
  };

  const onDelete = (id: string): void => run(async () => { await feishuRecipientsApi.remove(id); await load(); });

  const resolved = (rows ?? []).filter((r) => r.openId !== undefined).length;
  const pending = (rows ?? []).length - resolved;

  return (
    <div className="sch-roster">
      <div className="sch-overline" style={{ marginTop: "var(--space-5)" }}>飞书私信花名册</div>
      <p className="sch-subtitle">粘贴名单 → 解析邮箱/手机号为 open_id → 解析不到的手动补。open_id 是稳定地址。</p>
      {err !== null && <div className="sch-error" role="alert">{err}</div>}
      {note !== null && <div className="sch-note">{note}</div>}

      <div className="sch-field">
        <label>粘贴名单（每行：姓名,企业邮箱或手机号,角色）</label>
        <textarea
          className="sch-in"
          rows={3}
          placeholder={"张茜茜,zhangqianqian@aicoda.tech,运营负责人\nJimmy,13141209852,研发"}
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
        />
      </div>
      <div className="sch-field-row">
        <button className="btn ghost sm" disabled={busy || paste.trim().length === 0} onClick={onImport}>导入名单</button>
        {resolvable.length > 0 && (
          <select className="sch-in" style={{ maxWidth: 220 }} value={connId} onChange={(e) => setConnId(e.target.value)}>
            {resolvable.map((c) => (<option key={c.id} value={c.id}>{c.label}</option>))}
          </select>
        )}
        <button className="btn ghost sm" disabled={busy || resolvable.length === 0} onClick={onResolve}>解析 open_id</button>
        {rows !== null && <span className="sch-muted">已解析 {resolved} · 待补 {pending}</span>}
      </div>

      {rows !== null && rows.length > 0 && (
        <table className="sch-roster-table">
          <thead><tr><th>姓名</th><th>角色</th><th>邮箱/手机</th><th>open_id</th><th>来源</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td>{r.role || "—"}</td>
                <td>{r.email ?? r.mobile ?? "—"}</td>
                <td className="mono">{r.openId ?? "—"}</td>
                <td>{r.openId === undefined ? "待补漏" : r.source === "manual" ? "手动" : "已解析"}</td>
                <td className="sch-li-actions">
                  <button className="btn ghost sm" disabled={busy} onClick={() => onFill(r)}>补</button>
                  <button className="btn ghost sm danger" disabled={busy} onClick={() => onDelete(r.id)}>删</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


/** Human-readable byte size for the file tree (B / KB). */
function fmtBytes(n: number): string {
  return n < 1024 ? `${n} B` : `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
}

/** Relative time from a ms timestamp — past ("N 分钟前") or future ("N 分钟后"). */
function relTime(ms?: number): string {
  if (ms === undefined) return "—";
  const diff = Date.now() - ms;
  const past = diff >= 0;
  const suffix = past ? "前" : "后";
  const a = Math.abs(diff);
  if (a < 60_000) return past ? "刚刚" : "即将";
  if (a < 3_600_000) return `${Math.floor(a / 60_000)} 分钟${suffix}`;
  if (a < 86_400_000) return `${Math.floor(a / 3_600_000)} 小时${suffix}`;
  return `${Math.floor(a / 86_400_000)} 天${suffix}`;
}

/**
 * The card's upcoming-run line ("下次 …"), shown only for an active task — a
 * paused / failed / completed task gets no phantom next-run (the GitHub Actions
 * anti-pattern is showing a next time that will never fire). Relative, scannable.
 */
function nextRunLabel(task: ScheduledTask, running: boolean): string | null {
  if (running || task.status !== "active" || task.metadata?.autoPaused === true) return null;
  if (task.nextRunAt === undefined) return null;
  return `下次 ${relTime(task.nextRunAt)}`;
}

/** The card's last-run line: relative time tinted by run state. */
function lastRunState(task: ScheduledTask, running: boolean): { text: string; tone: string } {
  if (running) return { text: "正在运行", tone: "info" };
  if (task.metadata?.autoPaused === true) return { text: "已自动暂停", tone: "warn" };
  if (task.status === "failed") return { text: `失败 · ${relTime(task.lastRunAt)}`, tone: "err" };
  if (task.lastRunAt === undefined) return { text: "还没运行过", tone: "muted" };
  return { text: relTime(task.lastRunAt), tone: "muted" };
}

/**
 * The card's 产出 state: a downloadable "最新结果" (last run produced an artifact)
 * vs "未执行" (nothing to download yet). "result" gets a preview/download link on
 * the card so the user skips opening the detail.
 */
function producedState(task: ScheduledTask, running: boolean): { kind: "running" | "result" | "none"; label: string } {
  if (running) return { kind: "running", label: "正在生成…" };
  if (task.metadata?.lastOutcome === "produced") return { kind: "result", label: "最新结果" };
  return { kind: "none", label: "未执行" };
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }): JSX.Element {
  return (
    <div className={`sch-stat ${tone ?? ""}`}>
      <div className="sch-stat-lab">{label}</div>
      <div className="sch-stat-val">{value}</div>
    </div>
  );
}

function EmptyState({ onNew }: { onNew: () => void }): JSX.Element {
  return (
    <div className="sch-empty">
      <IconClock />
      <p>让小环替你定时做事，从一个例子开始。</p>
      <button className="btn" onClick={onNew}>
        <IconPlus /> 浏览模板
      </button>
    </div>
  );
}

function TaskCard(props: {
  task: ScheduledTask;
  showOwner: boolean;
  running: boolean;
  onRun: () => void;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onOpen: () => void;
}): JSX.Element {
  const { task, showOwner } = props;
  const [menuOpen, setMenuOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const lr = lastRunState(task, props.running);
  const next = nextRunLabel(task, props.running);
  const prod = producedState(task, props.running);
  const ownerSuffix = task.jobType === "agent.prompt" ? "routine" : "维护";
  const desc = task.description ?? task.prompt;
  return (
    <div
      className="rcard rcard-click"
      role="button"
      tabIndex={0}
      onClick={props.onOpen}
      onKeyDown={(e) => { if (e.key === "Enter") props.onOpen(); }}
    >
      <div className="rcard-head">
        {showOwner ? (
          <span className="owner">
            <span className="avatar">{(task.createdBy ?? "?").slice(0, 1).toUpperCase()}</span>
            {task.createdBy ?? "—"} · {ownerSuffix}
          </span>
        ) : (
          <span className="owner">{ownerSuffix === "routine" ? "小环 · routine" : "维护任务"}</span>
        )}
        {props.running ? (
          <span className="chip info">
            <span className="dot run pulse" /> 运行中
          </span>
        ) : (
          <span className={`chip ${task.metadata?.autoPaused === true ? "warn" : statusDot(task.status)}`}>
            <span className={`dot ${task.metadata?.autoPaused === true ? "warn" : statusDot(task.status)}`} />{" "}
            {task.metadata?.autoPaused === true ? "已自动暂停" : STATUS_LABEL[task.status]}
          </span>
        )}
      </div>
      <h4 className="rcard-name">{task.title}</h4>
      {desc !== undefined && <p className="rcard-desc">{desc}</p>}
      <div className="meta-row">
        <span className="mi"><IconClock /> {scheduleLabel(task)}</span>
        {next !== null && <span className="mi">{next}</span>}
        <span className={`mi ${lr.tone}`}><IconRefresh /> {lr.text}</span>
        <span className={`risk ${riskBadge(task.permission).tone}`}>{riskBadge(task.permission).label}</span>
        {[...connectorLabels(task.jobParams), ...skillLabels(task.jobParams)].map((u) => (
          <span key={u} className="sch-use-chip">{u}</span>
        ))}
      </div>
      <div className="rcard-foot" onClick={(e) => e.stopPropagation()}>
        {prod.kind === "result" ? (
          <button className="rcard-result" onClick={() => setPreviewing(true)} title="预览 / 下载产物">
            <IconFile /> {prod.label}
          </button>
        ) : (
          <span className="rcard-produced">{prod.label}</span>
        )}
        <div className="rcard-more">
          <button className="btn ghost sm" onClick={() => setMenuOpen((o) => !o)} title="更多" aria-label="更多">⋯</button>
          {menuOpen && (
            <>
              <div className="rcard-menu-backdrop" onClick={() => setMenuOpen(false)} />
              <div className="rcard-menu">
                <button disabled={props.running} onClick={() => { setMenuOpen(false); props.onRun(); }}>
                  {props.running ? "运行中…" : "立即运行"}
                </button>
                <button onClick={() => { setMenuOpen(false); props.onEdit(); }}>编辑</button>
                <button onClick={() => { setMenuOpen(false); props.onToggle(); }}>
                  {task.status === "paused" ? "恢复" : "暂停"}
                </button>
                <button className="danger" onClick={() => { setMenuOpen(false); props.onDelete(); }}>删除</button>
              </div>
            </>
          )}
        </div>
      </div>
      {previewing && <ResultPreview task={task} onClose={() => setPreviewing(false)} />}
    </div>
  );
}

/**
 * Lightweight result viewer opened straight from a card's 最新结果 link — fetches
 * the latest run's artifact and shows a preview + 下载, so the user skips opening
 * the full detail.
 */
function ResultPreview(props: { task: ScheduledTask; onClose: () => void }): JSX.Element {
  const [output, setOutput] = useState<string | null>(null);
  const [name, setName] = useState(`${props.task.title}.md`);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    schedulerApi
      .executions(props.task.id)
      .then((r) => {
        if (!live) return;
        const latest = r.executions.find(
          (e) => e.eventType === "task_completed" && typeof e.details.output === "string",
        );
        const out = typeof latest?.details.output === "string" ? latest.details.output : null;
        setOutput(out);
        if (typeof latest?.details.artifactName === "string") setName(latest.details.artifactName);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    return () => { live = false; };
  }, [props.task.id]);
  const download = (): void => {
    if (output === null) return;
    const blob = new Blob([output], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="sch-modal-bg" onClick={props.onClose}>
      <div className="sch-modal" style={{ width: "min(720px, 100%)" }} onClick={(e) => e.stopPropagation()}>
        <div className="sch-modal-head">
          <h3>{props.task.title} · 最新结果</h3>
          <button className="btn ghost sm" onClick={props.onClose}>关闭</button>
        </div>
        {loading && <p className="sch-muted">加载中…</p>}
        {!loading && output === null && <p className="sch-muted">最近一次没有可下载的产物。</p>}
        {output !== null && (
          <div className="sch-attach">
            <div className="sch-attach-file">
              <span className="sch-attach-glyph">MD</span>
              <div className="sch-attach-meta">
                <div className="sch-attach-name" title={name}>{name}</div>
                <div className="sch-attach-sub">Markdown · {fmtBytes(new Blob([output]).size)}</div>
              </div>
              <button className="btn ghost sm" onClick={download}>下载</button>
            </div>
            <div className="sch-attach-body"><Markdown>{output}</Markdown></div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Per-category icon (IconX set) for the template gallery tiles. */
const CATEGORY_ICON: Record<TaskTemplate["category"], (p: { className?: string }) => JSX.Element> = {
  reporting: IconChart,
  monitoring: IconActivity,
  triage: IconFlag,
  review: IconRefresh,
  maintenance: IconWrench,
};

function TemplateGallery(props: {
  onPick: (t: TaskTemplate | null) => void;
  onClose: () => void;
}): JSX.Element {
  const [templates, setTemplates] = useState<TaskTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void schedulerApi
      .templates()
      .then((r) => setTemplates(r.templates))
      .catch((e: unknown) => setError(friendlyError(e)));
  }, []);
  return (
    <div className="sch-modal-bg" onClick={props.onClose}>
      <div className="sch-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sch-modal-head">
          <div>
            <h3>挑一个让小环定时帮你做的事</h3>
            <p className="sch-subtitle">选一个模板填上你的情况，或从空白开始写自己的。</p>
          </div>
          <button className="btn ghost sm" onClick={props.onClose}>关闭</button>
        </div>
        {error !== null && <div className="sch-error" role="alert">{error}</div>}
        {error === null && templates === null && <p className="sch-muted">加载模板中…</p>}
        {error === null && templates !== null && templates.length === 0 && (
          <p className="sch-muted">暂无可用模板，可从空白开始。</p>
        )}
        <div className="sch-tg">
          <button className="tcard scratch" onClick={() => props.onPick(null)}>
            <span className="tcat scratch"><IconPlus /></span>
            <h5>从空白开始</h5>
            <p>自己写一句话告诉小环要做什么。</p>
          </button>
          {(templates ?? []).map((t) => {
            const CatIcon = CATEGORY_ICON[t.category];
            return (
            <button key={t.id} className="tcard" onClick={() => props.onPick(t)}>
              <span className={`tcat ${t.category}`}><CatIcon /></span>
              <h5>{t.name}</h5>
              <p>{t.intent ?? t.description}</p>
              <span className="tsched">
                <span className="tsched-when">
                  <span className="dot ok" /> {scheduleLabel({ recurrencePreset: t.recurrencePreset } as ScheduledTask)}
                </span>
                <span className={`risk ${riskBadge(t.permission).tone}`}>{riskBadge(t.permission).label}</span>
                {[...connectorLabels(t.jobParams), ...skillLabels(t.jobParams)].map((u) => (
                  <span key={u} className="sch-use-chip">{u}</span>
                ))}
              </span>
            </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** An @-mentionable capability (a configured connector or installed skill). */
interface Mentionable {
  kind: "connector" | "skill";
  key: string;
  display: string;
  /** Connector binding hint (e.g. "飞书 · 私信 花名册") so dm vs group is visible. */
  hint?: string;
}

/**
 * A prompt textarea with @-mention autocomplete (GPT-app style): typing `@`
 * opens a picker of connectors + skills; selecting one inserts `@<name>` into the
 * instruction AND activates that capability for the routine. Lets users reference
 * and enable capabilities inline while writing, instead of hunting checkboxes.
 */
/** A blue inline pill for an @-mention (non-editable, carries its name + kind). */
function makePill(display: string, kind: string): HTMLSpanElement {
  const span = document.createElement("span");
  span.className = `cap-pill${kind === "skill" ? " skill" : ""}`;
  span.contentEditable = "false";
  span.dataset.name = display;
  span.dataset.kind = kind;
  span.textContent = `@${display}`;
  return span;
}

/** Render initial content: plain text with `@<known name>` turned into pills. */
function renderInitial(el: HTMLElement, value: string, items: Mentionable[]): void {
  el.textContent = "";
  if (value.length === 0) return;
  const byLen = [...items].sort((a, b) => b.display.length - a.display.length);
  let buf = "";
  const flush = (): void => {
    if (buf.length > 0) {
      el.appendChild(document.createTextNode(buf));
      buf = "";
    }
  };
  let i = 0;
  while (i < value.length) {
    if (value[i] === "@") {
      const hit = byLen.find((it) => value.startsWith(`@${it.display}`, i));
      if (hit !== undefined) {
        flush();
        el.appendChild(makePill(hit.display, hit.kind));
        i += 1 + hit.display.length;
        continue;
      }
    }
    buf += value[i];
    i += 1;
  }
  flush();
}

/** Serialize the contenteditable back to plain text (pills → `@name`). */
function serializeEditor(el: HTMLElement): string {
  let out = "";
  el.childNodes.forEach((n) => {
    if (n.nodeType === Node.TEXT_NODE) out += n.textContent ?? "";
    else if (n instanceof HTMLElement && n.dataset.name !== undefined) out += `@${n.dataset.name}`;
    else out += n.textContent ?? "";
  });
  return out;
}

/**
 * Prompt editor with @-mention pills (GPT-app style). A contenteditable surface
 * renders `@连接器/@技能` as inline blue pills; typing `@` opens a picker;
 * selecting inserts a pill at the caret AND activates that capability. The plain
 * value (pills → `@name`) flows out via onChange. IME-safe (skips detection while
 * composing). Init runs once so typing never resets the caret.
 */
function PromptWithMentions(props: {
  value: string;
  onChange: (v: string) => void;
  items: Mentionable[];
  onActivate: (m: Mentionable) => void;
  placeholder?: string;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const composing = useRef(false);
  const inited = useRef(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [idx, setIdx] = useState(0);
  // The picker is rendered fixed-positioned off the editor's rect so it escapes
  // the modal's `overflow:auto` clip (it was cut off near the modal's bottom).
  const [popPos, setPopPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const matches = useMemo(
    () => props.items.filter((m) => m.display.toLowerCase().includes(query.toLowerCase())).slice(0, 8),
    [props.items, query],
  );

  // Render the initial content ONCE — wait for items if the value has @mentions
  // (so a template's @名称 renders as pills, not literal text).
  useEffect(() => {
    const el = ref.current;
    if (el === null || inited.current) return;
    if (props.items.length === 0 && props.value.includes("@")) return;
    inited.current = true;
    renderInitial(el, props.value, props.items);
  }, [props.items, props.value]);

  const emit = (): void => {
    if (ref.current !== null) props.onChange(serializeEditor(ref.current));
  };

  /** Detect an `@token` immediately before the caret and open the picker. */
  const detectTrigger = (): void => {
    if (composing.current) return;
    const sel = window.getSelection();
    if (sel === null || sel.rangeCount === 0) return setOpen(false);
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return setOpen(false);
    const before = (node.textContent ?? "").slice(0, range.startOffset);
    const at = before.lastIndexOf("@");
    if (at >= 0 && !/\s/.test(before.slice(at + 1))) {
      setQuery(before.slice(at + 1));
      setIdx(0);
      if (ref.current !== null) {
        const r = ref.current.getBoundingClientRect();
        setPopPos({ top: r.bottom + 4, left: r.left, width: r.width });
      }
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const pick = (m: Mentionable): void => {
    const sel = window.getSelection();
    if (sel === null || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType === Node.TEXT_NODE) {
      const txt = node.textContent ?? "";
      const at = txt.slice(0, range.startOffset).lastIndexOf("@");
      const parent = node.parentNode;
      if (at >= 0 && parent !== null) {
        const after = txt.slice(range.startOffset);
        node.textContent = txt.slice(0, at);
        const pill = makePill(m.display, m.kind);
        const space = document.createTextNode(" ");
        const afterNode = document.createTextNode(after);
        parent.insertBefore(afterNode, node.nextSibling);
        parent.insertBefore(space, afterNode);
        parent.insertBefore(pill, space);
        const r = document.createRange();
        r.setStart(space, 1);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
      }
    }
    props.onActivate(m);
    setOpen(false);
    emit();
    ref.current?.focus();
  };

  return (
    <div className="sch-mention-wrap">
      <div
        ref={ref}
        className="sch-in sch-editable"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={props.placeholder}
        onInput={() => { emit(); detectTrigger(); }}
        onCompositionStart={() => { composing.current = true; }}
        onCompositionEnd={() => { composing.current = false; emit(); detectTrigger(); }}
        onClick={detectTrigger}
        onKeyUp={(e) => { if (!["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(e.key)) detectTrigger(); }}
        onKeyDown={(e) => {
          if (!open || matches.length === 0) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => (i + 1) % matches.length); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => (i - 1 + matches.length) % matches.length); }
          else if (e.key === "Enter") { e.preventDefault(); pick(matches[idx] ?? matches[0]!); }
          else if (e.key === "Escape") { setOpen(false); }
        }}
      />
      {open && matches.length > 0 && (
        <div
          className="sch-mention-pop"
          style={popPos !== null ? { position: "fixed", top: popPos.top, left: popPos.left, width: popPos.width, right: "auto" } : undefined}
        >
          {matches.map((m, i) => (
            <button
              key={`${m.kind}:${m.key}`}
              type="button"
              className={`sch-mention-item ${i === idx ? "on" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); pick(m); }}
            >
              <span className="sch-mention-at">@</span>
              <span className="sch-mention-name">{m.display}</span>
              <span className="sch-mention-kind">{m.kind === "connector" ? (m.hint ?? "连接器") : "技能"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Playbook-style task configuration. The form hides the internal job type and
 * the observe/assist/act permission — a support agent only sees: a title, the
 * plain-language instruction for 小环 (the prompt, for routines), and how often.
 * A quiet risk badge conveys what it may touch. Maintenance jobs (no prompt)
 * show a read-only "它会做什么" instead of an editable instruction.
 */
function TaskForm(props: {
  defaults: FormDefaults;
  isEdit?: boolean;
  onSubmit: (body: CreateTaskBody) => Promise<ScheduledTask | null>;
  onClose: () => void;
  /** Open the 集成 panel (from an unconfigured connector's 去配置 CTA). */
  onConfigure?: () => void;
  /** Bumped when the 集成 panel closes, to re-fetch connector status. */
  connVersion?: number;
}): JSX.Element {
  const d = props.defaults;
  const jobType = d.jobType ?? "agent.prompt";
  const permission = d.permission ?? "assist";
  const isRoutine = jobType === "agent.prompt";

  const [title, setTitle] = useState(d.title ?? "");
  const [prompt, setPrompt] = useState(d.prompt ?? "");
  const [preset, setPreset] = useState(d.recurrencePreset ?? "weekdays");
  // Step 2 leads with natural language ("每个工作日早上9点"); 精确选择 is the escape.
  const [nl, setNl] = useState("");
  const [precise, setPrecise] = useState(false);
  const parsedSchedule = useMemo(() => parseScheduleZh(nl), [nl]);
  const [trigger, setTrigger] = useState<"schedule" | "webhook">(d.triggerType ?? "schedule");
  // Wizard: which step, whether a submit is in flight, and (for a new webhook
  // routine) the created task so the last step can show its trigger URL.
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<ScheduledTask | null>(null);
  const risk = riskBadge(permission);
  const sources = sourceFields(jobType);

  // Editable pull-source values, keyed by field; list fields use a newline string.
  const [source, setSource] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of sources) {
      const v = d.jobParams?.[f.key];
      if (f.kind === "list" && Array.isArray(v)) init[f.key] = v.join("\n");
      else if (f.kind === "text" && typeof v === "string") init[f.key] = v;
      else init[f.key] = "";
    }
    return init;
  });

  // Installed EXTERNAL skills the routine may activate (prompt routines only).
  const [skills, setSkills] = useState<SchedulerSkillInfo[]>([]);
  const [pickedSkills, setPickedSkills] = useState<Set<string>>(() => {
    const raw = d.jobParams?.skills;
    return new Set(Array.isArray(raw) ? raw.filter((x): x is string => typeof x === "string") : []);
  });
  useEffect(() => {
    if (!isRoutine) return;
    void schedulerApi.skills().then((r) => setSkills(r.skills)).catch(() => setSkills([]));
  }, [isRoutine]);
  const toggleSkill = (name: string): void =>
    setPickedSkills((s) => {
      const next = new Set(s);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  // Selectable data connectors (tickets / feishu / changes). Pre-checked from the
  // template's job_params.connectors; the user can add/remove any.
  const [connectors, setConnectors] = useState<SchedulerConnectorInfo[]>([]);
  const [notifyOnChange, setNotifyOnChange] = useState(d.jobParams?.notifyOnChangeOnly === true);
  const [pickedConnectors, setPickedConnectors] = useState<Set<string>>(() => {
    const raw = d.jobParams?.connectors;
    return new Set(Array.isArray(raw) ? raw.filter((x): x is string => typeof x === "string") : []);
  });
  useEffect(() => {
    if (!isRoutine) return;
    void schedulerApi.connectors().then((r) => setConnectors(r.connectors)).catch(() => setConnectors([]));
  }, [isRoutine, props.connVersion]);
  const toggleConnector = (id: string): void =>
    setPickedConnectors((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // "会用到" reflects the currently-picked connectors (labels from the registry).
  const uses = [...pickedConnectors].map((id) => connectors.find((c) => c.id === id)?.label ?? id);

  // A routine needs a non-empty instruction; a maintenance job just a title.
  const valid = title.trim().length > 0 && (!isRoutine || prompt.trim().length > 0);

  // Merge edited source fields + picked skills into job_params (preserving connectors).
  const buildJobParams = (): Record<string, unknown> | undefined => {
    const merged: Record<string, unknown> = { ...(d.jobParams ?? {}) };
    for (const f of sources) {
      const raw = (source[f.key] ?? "").trim();
      if (raw.length === 0) {
        delete merged[f.key]; // blank → fall back to the server default
      } else if (f.kind === "list") {
        merged[f.key] = raw.split("\n").map((s) => s.trim()).filter((s) => s.length > 0);
      } else {
        merged[f.key] = raw;
      }
    }
    if (pickedSkills.size > 0) merged.skills = [...pickedSkills];
    else delete merged.skills;
    if (pickedConnectors.size > 0) merged.connectors = [...pickedConnectors];
    else delete merged.connectors;
    if (notifyOnChange) merged.notifyOnChangeOnly = true;
    else delete merged.notifyOnChangeOnly;
    return Object.keys(merged).length > 0 ? merged : undefined;
  };

  const submit = (): void => {
    const jobParams = buildJobParams();
    setSubmitting(true);
    void props
      .onSubmit({
        title: title.trim(),
        jobType,
        permission,
        ...(isRoutine && prompt.trim().length > 0 ? { prompt: prompt.trim() } : {}),
        ...(d.description !== undefined ? { description: d.description } : {}),
        ...(d.priority !== undefined ? { priority: d.priority } : {}),
        ...(jobParams !== undefined ? { jobParams } : {}),
        triggerType: trigger,
        ...(trigger === "schedule"
          ? !precise && parsedSchedule !== null
            ? { cronExpression: parsedSchedule.cron }
            : preset.length > 0
              ? { recurrencePreset: preset }
              : {}
          : {}),
      })
      .then((task) => {
        // A webhook routine (newly created OR edited to webhook): stay open and
        // show its trigger URL + 用法 instead of closing silently.
        if (task !== null && trigger === "webhook" && webhookUrl(task) !== null) {
          setCreated(task);
        } else {
          props.onClose();
        }
      })
      .finally(() => setSubmitting(false));
  };

  // Wizard steps. 做什么 → 何时触发 → 用什么·确认. Maintenance jobs skip the
  // prompt/skills but keep the same shape so the flow feels consistent.
  const STEPS = ["做什么", "何时", "确认"];
  const isLast = step === STEPS.length - 1;
  // Step 0 needs a title (+ a prompt for routines) before advancing.
  const canAdvance = step !== 0 || valid;

  // Post-create success: a webhook routine — show how to fire it.
  if (created !== null) {
    const url = webhookUrl(created) ?? "";
    return (
      <div className="sch-modal-bg" onClick={props.onClose}>
        <div className="sch-modal narrow" onClick={(e) => e.stopPropagation()}>
          <div className="sch-modal-head">
            <div>
              <h3>{props.isEdit === true ? "已保存，用这个链接触发" : "建好了，用这个链接触发"}</h3>
              <p className="sch-subtitle">「{created.title}」不按时间跑。外部一调用，小环就跑一次。</p>
            </div>
            <button className="btn ghost sm" onClick={props.onClose}>关闭</button>
          </div>
          <div className="sch-field">
            <label>触发链接</label>
            <div className="sch-webhook">
              <code className="mono">{url}</code>
              <button className="btn" onClick={() => void navigator.clipboard.writeText(url)}>拷贝触发链接</button>
            </div>
            <p className="sch-note">把它配成外部系统的回调即可：GitHub/飞书 事件、监控告警，或另一个任务跑完后调它。</p>
          </div>
          <details className="sch-advanced">
            <summary>给开发者</summary>
            <pre className="sch-code"><code>curl -X POST "{url}"</code></pre>
            <p className="sch-note">无需登录，链接里的密钥就是凭证，请妥善保管、不要外泄。</p>
          </details>
          <div className="sch-form-actions">
            <button className="btn" onClick={props.onClose}>完成</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sch-modal-bg" onClick={props.onClose}>
      <div className="sch-modal narrow" onClick={(e) => e.stopPropagation()}>
        <div className="sch-modal-head">
          <div>
            <h3>{props.isEdit === true ? "编辑任务" : isRoutine ? "让小环定时帮你做这件事" : "新建维护任务"}</h3>
            {d.intent !== undefined && <p className="sch-subtitle">{d.intent}</p>}
          </div>
          <button className="btn ghost sm" onClick={props.onClose}>关闭</button>
        </div>

        <div className="sch-steps">
          {STEPS.map((s, i) => (
            <div key={s} className={`sch-step ${i === step ? "on" : ""} ${i < step ? "done" : ""}`}>
              <span className="sch-step-n">{i < step ? "✓" : i + 1}</span>
              <span className="sch-step-t">{s}</span>
            </div>
          ))}
        </div>

        {step === 0 && (
          <>
            <div className="sch-field">
              <label>给它起个名字</label>
              <input
                className="sch-in"
                value={title}
                placeholder="如 每日工单风险汇总"
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            {isRoutine ? (
              <div className="sch-field">
                <label>要小环做什么</label>
                <p className="sch-hint">用一句话或几步说清楚。打 <b>@</b> 可引用你配置的连接器/技能（按名字，如 @cyclecare、@飞书通知），选中即启用。</p>
                <PromptWithMentions
                  value={prompt}
                  onChange={setPrompt}
                  placeholder={"如：读代码仓库最近的 issue 汇总，再发到飞书群。打 @ 引用你配置的连接器。"}
                  items={[
                    ...connectors
                      .filter((c) => c.configured)
                      .map((c) => ({
                        kind: "connector" as const,
                        key: c.id,
                        display: c.label,
                        ...(c.boundResource !== undefined ? { hint: c.boundResource } : {}),
                      })),
                    ...skills.map((s) => ({ kind: "skill" as const, key: s.name, display: s.name })),
                  ]}
                  onActivate={(m) => {
                    if (m.kind === "connector") setPickedConnectors((s) => new Set(s).add(m.key));
                    else setPickedSkills((s) => new Set(s).add(m.key));
                  }}
                />
                {(pickedConnectors.size > 0 || pickedSkills.size > 0) && (
                  <div className="sch-active-caps">
                    <span className="sch-active-label">已启用</span>
                    {[...pickedConnectors].map((id) => (
                      <span key={`c-${id}`} className="sch-cap-chip">
                        @{connectors.find((c) => c.id === id)?.label ?? id}
                        <button type="button" onClick={() => toggleConnector(id)} aria-label="移除">×</button>
                      </span>
                    ))}
                    {[...pickedSkills].map((n) => (
                      <span key={`s-${n}`} className="sch-cap-chip skill">
                        @{n}
                        <button type="button" onClick={() => toggleSkill(n)} aria-label="移除">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="sch-field">
                <label>它会做什么</label>
                <p className="sch-readonly">{d.description ?? "固定的维护动作。"}</p>
                {(() => {
                  // Maintenance jobs have fixed connectors/skills (by job type, not
                  // user-picked) — show them read-only so an editor sees "原来的样子"
                  // instead of a blank form that looks like the provenance was lost.
                  const uses = [...connectorLabels(d.jobParams), ...skillLabels(d.jobParams)];
                  return uses.length > 0 ? (
                    <div className="sch-active-caps" style={{ marginTop: "8px" }}>
                      <span className="sch-active-label">会用到</span>
                      {uses.map((u) => <span key={u} className="sch-use-chip">{u}</span>)}
                    </div>
                  ) : null;
                })()}
              </div>
            )}
          </>
        )}

        {step === 1 && (
          <div className="sch-field">
            <label>什么时候触发</label>
            <div className="pillgroup">
              <span className={`spill ${trigger === "schedule" ? "on" : ""}`} onClick={() => setTrigger("schedule")}>按排程</span>
              <span className={`spill ${trigger === "webhook" ? "on" : ""}`} onClick={() => setTrigger("webhook")}>由外部事件触发</span>
            </div>
            {trigger === "schedule" ? (
              precise ? (
                <>
                  <select className="sch-in" style={{ marginTop: "var(--space-2)" }} value={preset} onChange={(e) => setPreset(e.target.value)}>
                    {PRESET_OPTIONS.map((o) => (
                      <option key={o.value.length > 0 ? o.value : "once"} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <p className="sch-note">按这个时间自动开始；随时可暂停。 <span className="sch-link" onClick={() => setPrecise(false)}>用自然语言</span></p>
                </>
              ) : (
                <>
                  <input
                    className="sch-in"
                    style={{ marginTop: "var(--space-2)" }}
                    value={nl}
                    placeholder="如：每个工作日早上 9 点 / 每天下午下班前 / 每周一 10:30"
                    onChange={(e) => setNl(e.target.value)}
                  />
                  {parsedSchedule !== null ? (
                    <div className="sch-confirm sch-sched-confirm">
                      <span className="chip-ok">✓ {parsedSchedule.label}</span>
                      <span className="caption mono">{parsedSchedule.cron}</span>
                    </div>
                  ) : nl.trim().length > 0 ? (
                    <p className="sch-note">没太懂，换种说法（如"每天上午 9 点"），或 <span className="sch-link" onClick={() => setPrecise(true)}>精确选择</span>。</p>
                  ) : (
                    <p className="sch-note">用一句话说什么时候跑；也可 <span className="sch-link" onClick={() => setPrecise(true)}>精确选择</span>。</p>
                  )}
                </>
              )
            ) : (
              <p className="sch-note">不按时间跑。建好后会给你一个触发链接，外部系统一调用就跑一次（比如"生成完→回调"）。下一步建好就能拷贝链接。</p>
            )}
          </div>
        )}

        {step === 2 && (
          <>
            {sources.length > 0 && (
              <div className="sch-source">
                <div className="sch-overline">来源配置</div>
                <p className="sch-hint">从哪里拉取。留空则用服务端默认配置。</p>
                {sources.map((f) => (
                  <div className="sch-field" key={f.key}>
                    <label>{f.label}</label>
                    {f.kind === "list" ? (
                      <textarea
                        className="sch-in sch-textarea"
                        rows={2}
                        value={source[f.key] ?? ""}
                        placeholder={f.placeholder ?? ""}
                        onChange={(e) => setSource((s) => ({ ...s, [f.key]: e.target.value }))}
                      />
                    ) : (
                      <input
                        className="sch-in"
                        value={source[f.key] ?? ""}
                        placeholder={f.placeholder ?? ""}
                        onChange={(e) => setSource((s) => ({ ...s, [f.key]: e.target.value }))}
                      />
                    )}
                    {f.hint !== undefined && <p className="sch-note">{f.hint}</p>}
                  </div>
                ))}
              </div>
            )}

            {isRoutine && connectors.length > 0 && (
              <div className="sch-source">
                <div className="sch-overline">连接数据（可选）</div>
                <p className="sch-hint">勾选小环可以读/写的数据源。读类只查看；写类（标「写」）会真正发出去。未配置的需先去「集成」连接。</p>
                <div className="sch-conns">
                  {connectors.map((c) => (
                    <div key={c.id} className={`sch-conn ${c.configured ? "" : "off"} ${pickedConnectors.has(c.id) ? "on" : ""}`}>
                      <label className="sch-conn-main" title={c.description}>
                        <input
                          type="checkbox"
                          disabled={!c.configured}
                          checked={pickedConnectors.has(c.id)}
                          onChange={() => toggleConnector(c.id)}
                        />
                        <span className="sch-conn-text">
                          <span className="sch-conn-name">
                            {c.label}
                            {c.permission !== "observe" && <span className="sch-skill-tag" style={{ marginLeft: 6 }}>写</span>}
                          </span>
                          <span className="sch-conn-sub">
                            {c.configured ? (c.boundResource ?? "已配置") : "未配置"}
                          </span>
                        </span>
                      </label>
                      {!c.configured && props.onConfigure !== undefined && (
                        <button className="btn ghost sm" onClick={props.onConfigure}>去配置</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isRoutine && skills.length > 0 && (
              <div className="sch-source">
                <div className="sch-overline">启用技能（团队，可选）</div>
                <p className="sch-hint">勾选已安装的团队技能，小环会带上它的指令；带脚本的技能脚本会在沙箱里运行。</p>
                <div className="sch-skills">
                  {skills.map((sk) => (
                    <label key={`${sk.tenant}/${sk.name}`} className={`sch-skill ${pickedSkills.has(sk.name) ? "on" : ""}`} title={sk.description}>
                      <input type="checkbox" checked={pickedSkills.has(sk.name)} onChange={() => toggleSkill(sk.name)} />
                      <span>{sk.name}</span>
                      {sk.hasScripts && <span className="sch-skill-tag">脚本待启用</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {isRoutine && (
              <label className="sch-quiet">
                <input type="checkbox" checked={notifyOnChange} onChange={(e) => setNotifyOnChange(e.target.checked)} />
                <span>
                  仅在有变化时通知
                  <span className="sch-note">监控类任务推荐：和上次结果相比没有变化就不打扰你。</span>
                </span>
              </label>
            )}

            <div className="sch-confirm">
              <div className="sch-kv"><span>名字</span><span>{title.trim().length > 0 ? title : "—"}</span></div>
              <div className="sch-kv"><span>触发</span><span>{trigger === "webhook" ? "由外部事件触发" : (!precise && parsedSchedule !== null ? parsedSchedule.label : (PRESET_OPTIONS.find((o) => o.value === preset)?.label ?? preset))}</span></div>
              {uses.length > 0 && (
                <div className="sch-kv"><span>会用到</span><span>{uses.join(" · ")}</span></div>
              )}
            </div>

            <div className="sch-risk-row">
              <span className={`risk ${risk.tone}`}>{risk.label}</span>
              <span className="sch-note">
                {permission === "observe"
                  ? "只查看和整理信息，不改动任何东西。"
                  : "可能会创建工单、更新内容等操作。"}
              </span>
            </div>
          </>
        )}

        <div className="sch-form-actions">
          {step > 0 ? (
            <button className="btn ghost" onClick={() => setStep((s) => s - 1)}>上一步</button>
          ) : (
            <button className="btn ghost" onClick={props.onClose}>取消</button>
          )}
          {isLast ? (
            <button className="btn" disabled={!valid || submitting} onClick={submit}>
              {submitting ? "提交中…" : props.isEdit === true ? "保存" : "创建"}
            </button>
          ) : (
            <button className="btn" disabled={!canAdvance} onClick={() => setStep((s) => s + 1)}>下一步</button>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskDetail(props: {
  task: ScheduledTask;
  running: boolean;
  onRunAgain: () => void;
  onToggle: () => void;
  onEdit: () => void;
  onClose: () => void;
}): JSX.Element {
  const { task } = props;
  const [events, setEvents] = useState<SchedulerAuditEntry[] | null>(null);

  const load = useCallback(async (): Promise<SchedulerAuditEntry[]> => {
    try {
      const r = await schedulerApi.executions(task.id);
      setEvents(r.executions);
      return r.executions;
    } catch {
      setEvents([]);
      return [];
    }
  }, [task.id]);

  // Poll the timeline until the run reaches a terminal outcome (completed/failed)
  // — an agent run can take 30-60s, so a fixed short poll would stop too early and
  // leave "触发" looking stuck. Capped so it never polls forever.
  useEffect(() => {
    let stopped = false;
    let n = 0;
    const tick = async (): Promise<void> => {
      const evs = await load();
      n += 1;
      const terminal = evs.some((e) => e.eventType === "task_completed" || e.eventType === "task_failed");
      if (!stopped && !terminal && n < 48) setTimeout(() => void tick(), 2500); // ~2 min cap
    };
    void tick();
    return () => {
      stopped = true;
    };
  }, [load]);

  // The most recent outcome (completed/failed) is the headline "result".
  const evs = events ?? [];
  const latest = evs.find((e) => e.eventType === "task_completed" || e.eventType === "task_failed");
  // Duration of that run = its time minus the trigger that preceded it.
  const runMs = (() => {
    if (latest === undefined) return null;
    const i = evs.indexOf(latest);
    const trig = evs.slice(i + 1).find((e) => e.eventType === "task_triggered");
    return trig !== undefined ? latest.createdAt - trig.createdAt : null;
  })();
  // A produced document (e.g. a briefing) — preview + download.
  const output = typeof latest?.details.output === "string" ? latest.details.output : null;
  const artifactName =
    typeof latest?.details.artifactName === "string" ? latest.details.artifactName : "result.md";
  // Short, one-line timeline label — keep the full agent output out of the list
  // (it lives in the 产物 section), so the timeline stays a tidy event log.
  // Honest two-layer status: 已产出 (real artifact) vs 已执行·空结果 (ran, nothing
  // to show) vs 失败 / 已自动暂停 — so the dot color/label guides what to open.
  const tlLabel = (e: SchedulerAuditEntry): string => {
    if (e.eventType === "task_completed") return e.details.outcome === "empty" ? "已执行 · 空结果" : "已产出";
    if (e.eventType === "task_failed") return (e.message.split("\n")[0] ?? "失败").slice(0, 48);
    if (e.eventType === "task_auto_paused") return "已自动暂停";
    if (e.eventType === "task_triggered") return "触发";
    return (e.message.split("\n")[0] ?? "").slice(0, 48);
  };
  const tlTone = (e: SchedulerAuditEntry): string => {
    if (e.eventType === "task_completed") return e.details.outcome === "empty" ? "muted" : "ok";
    if (e.eventType === "task_failed") return "err";
    if (e.eventType === "task_auto_paused") return "warn";
    return "info";
  };
  const download = (): void => {
    if (output === null) return;
    const blob = new Blob([output], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = artifactName;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="sch-modal-bg" onClick={props.onClose}>
      <div className="sch-modal sch-detail" onClick={(e) => e.stopPropagation()}>
        <div className="sch-modal-head">
          <div className="sch-dt-title">
            <h3>{task.title}</h3>
            <span className={`chip ${task.metadata?.autoPaused === true ? "warn" : statusDot(task.status)}`}>
              <span className={`dot ${task.metadata?.autoPaused === true ? "warn" : statusDot(task.status)}`} />{" "}
              {task.metadata?.autoPaused === true ? "已自动暂停" : STATUS_LABEL[task.status]}
            </span>
          </div>
          <button className="btn ghost sm" onClick={props.onClose}>关闭</button>
        </div>

        {/* A run is in flight — make it unmistakable that it's working. */}
        {props.running && (
          <div className="sch-running">
            <span className="dot run pulse" /> 正在运行…<span className="sch-note" style={{ margin: 0 }}>完成后这里会显示结果，可能要几十秒。</span>
          </div>
        )}

        {/* Left = 任务描述 (top) + 执行结果 (bottom: the run's output as a
            downloadable attachment). Right = overview facts + actions + history. */}
        <div className="sch-dt-body">
          <div className="sch-dt-left">
            <section className="sch-dt-sec">
              <div className="sch-overline">任务描述</div>
              <p className="sch-readonly">{task.description ?? task.prompt ?? "暂无描述。"}</p>
            </section>

            <section className="sch-dt-sec">
              <div className="sch-overline">执行结果</div>
              {latest === undefined && <p className="sch-muted">还没有运行过，点右侧「立即运行」试一次。</p>}
              {latest !== undefined && (
                <>
                  <div className={`sch-result ${latest.eventType === "task_failed" ? "err" : "ok"}`}>
                    <div className="sch-result-head">
                      {latest.eventType === "task_failed" ? "上次没有成功" : "上次运行完成"} · {fmt(latest.createdAt)}
                      {runMs !== null && ` · 用时 ${Math.max(1, Math.round(runMs / 1000))}s`}
                    </div>
                    {output === null && <div className="sch-result-body">{latest.message}</div>}
                  </div>
                  {output !== null ? (
                    <div className="sch-attach">
                      <div className="sch-attach-file">
                        <span className="sch-attach-glyph">MD</span>
                        <div className="sch-attach-meta">
                          <div className="sch-attach-name" title={artifactName}>{artifactName}</div>
                          <div className="sch-attach-sub">Markdown · {fmtBytes(new Blob([output]).size)}</div>
                        </div>
                        <button className="btn ghost sm" onClick={download}>下载</button>
                      </div>
                      <div className="sch-attach-body"><Markdown>{output}</Markdown></div>
                    </div>
                  ) : (
                    <p className="sch-muted">这次没有生成可下载的文件。</p>
                  )}
                </>
              )}
            </section>
          </div>

          <div className="sch-dt-right">
            <div className="sch-facts">
              <div className="sch-kv"><span>排程</span><span>{scheduleLabel(task)}</span></div>
              {task.cronExpression !== undefined && (
                <div className="sch-kv"><span>cron</span><span className="mono">{task.cronExpression}</span></div>
              )}
              <div className="sch-kv"><span>下次运行</span><span>{fmt(task.nextRunAt)}</span></div>
              <div className="sch-kv"><span>上次运行</span><span>{fmt(task.lastRunAt)}</span></div>
              <div className="sch-kv"><span>产出物</span><span>{output !== null ? artifactName : "—"}</span></div>
              {sourceSummary(task.jobType, task.jobParams) !== null && (
                <div className="sch-kv"><span>来源</span><span>{sourceSummary(task.jobType, task.jobParams)}</span></div>
              )}
              <div className="sch-kv"><span>影响范围</span><span className={`risk ${riskBadge(task.permission).tone}`}>{riskBadge(task.permission).label}</span></div>
              {(() => {
                // 会用到: the connector(s) + skill(s) this task actually uses, parsed
                // from job_params — so the detail shows provenance, not just the card.
                const uses = [...connectorLabels(task.jobParams), ...skillLabels(task.jobParams)];
                return uses.length > 0 ? (
                  <div className="sch-kv">
                    <span>会用到</span>
                    <span>{uses.map((u) => <span key={u} className="sch-use-chip">{u}</span>)}</span>
                  </div>
                ) : null;
              })()}
              {webhookUrl(task) !== null && (
                <div className="sch-webhook">
                  <code className="mono">{webhookUrl(task)}</code>
                  <button className="btn ghost sm" onClick={() => void navigator.clipboard.writeText(webhookUrl(task) ?? "")}>复制</button>
                </div>
              )}
            </div>

            <div className="sch-side-actions">
              <button className="btn" onClick={props.onRunAgain} disabled={props.running}>
                {props.running ? "运行中…" : "立即运行"}
              </button>
              <button className="btn ghost" onClick={props.onToggle}>{task.status === "paused" ? "恢复" : "暂停"}</button>
              <button className="btn ghost" onClick={props.onEdit}>编辑</button>
            </div>

            <div className="sch-side-hd">
              <div className="sch-overline">执行历史</div>
              <button className="btn ghost sm" onClick={() => void load()}>刷新</button>
            </div>
            <div className="sch-timeline">
              {events === null && <p className="sch-muted">加载中…</p>}
              {events !== null && events.length === 0 && <p className="sch-muted">还没有运行过</p>}
              {evs.map((e) => (
                <div key={e.id} className="tl-item">
                  <span className={`tdot ${tlTone(e)}`} />
                  <div className="tl-msg">{tlLabel(e)}</div>
                  <div className="tl-time">{fmt(e.createdAt)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
